import * as React from 'react';

import { cn } from './lib/cn';

export interface RadioProps {
  name: string;
  checked: boolean;
  onSelect: () => void;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  value?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  className?: string;
}

/**
 * The Batch-2 radio: a 16 px circle, filled with the interactive token when
 * selected. Promoted from the operator app (IMM-981), where the first cut was a
 * bare `<input type="radio">` that took keyboard focus with no visible ring.
 *
 * The same shape as {@link Checkbox}, on purpose: a visually hidden native input
 * inside the caller's label, with the drawn control beside it. The native input
 * brings the group behaviour — one `name`, arrow keys moving the selection,
 * form participation — and the drawn span only has to look right.
 *
 * `onSelect` takes no argument. A radio is only ever turned on by a gesture;
 * turning it off is another radio in the group being turned on, so a callback
 * carrying a boolean would always carry `true`.
 */
const Radio = React.forwardRef<HTMLInputElement, RadioProps>(
  ({ name, checked, onSelect, disabled = false, invalid = false, id, value, className, ...aria }, ref) => (
    <span className={cn('relative inline-flex h-4 w-4 shrink-0', className)}>
      <input
        ref={ref}
        id={id}
        type="radio"
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        onChange={() => onSelect()}
        className="peer absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
        {...aria}
      />
      <span
        aria-hidden
        className={cn(
          'pointer-events-none flex h-4 w-4 items-center justify-center rounded-full border',
          'transition-colors duration-micro ease-out',
          'peer-focus-visible:shadow-focus',
          checked ? 'border-interactive bg-interactive' : 'border-neutral-300 bg-surface',
          invalid && 'border-danger-500',
          disabled && (checked ? 'border-neutral-300 bg-neutral-300' : 'border-neutral-200 bg-neutral-50'),
        )}
      >
        {checked && <span className={cn('h-1.5 w-1.5 rounded-full bg-interactive-on', disabled && 'bg-neutral-50')} />}
      </span>
    </span>
  ),
);
Radio.displayName = 'Radio';

export { Radio };
